/**
 * Sign in / create an account — a clean centered card under the WarnixAI
 * mark, on the page grey. The behaviour is exactly what it was: the same
 * calls (/api/auth/login, /api/auth/signup), the same error mapping, the
 * account's stored language applied on sign-in, the browser's time zone and
 * the form's language sent on sign-up, then /dashboard or /onboarding.
 *
 * The language picker stays first on the page: someone who cannot read
 * Hebrew must be able to switch BEFORE they try to understand the form.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/common/Button";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { api } from "@/lib/api";
import { useAuth, type AuthUser } from "@/lib/auth";
import { changeLanguage, currentLanguage } from "@/lib/i18n";
import { BrandMark } from "@/components/people/BrandMark";
import { TextField } from "@/components/people/Field";
import { Notice } from "@/components/people/Notice";
import { PasswordField } from "@/components/people/PasswordField";
import { authErrorKey } from "@/components/people/authErrors";

interface LoginResponse {
  token: string;
  user: AuthUser;
}

type Mode = "login" | "signup";

/**
 * The site's timezone at signup. The browser knows it, and a Romanian or
 * Thai crew supervising a site abroad is exactly the case the 20-language
 * work created — so we no longer hardcode Asia/Jerusalem. Falls back to it
 * only when the browser refuses to tell us.
 */
function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Jerusalem";
  } catch {
    return "Asia/Jerusalem";
  }
}

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const setSession = useAuth((s) => s.setSession);
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [siteName, setSiteName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "login") {
        const res = await api<LoginResponse>("/api/auth/login", {
          method: "POST",
          body: { email: email.trim().toLowerCase(), password },
          skipAuth: true,
        });
        setSession(res.token, res.user);
        // The account's stored language wins over whatever this device was
        // showing — a worker signing in on a shared phone gets their own.
        if (res.user.lang && res.user.lang !== currentLanguage()) {
          await changeLanguage(res.user.lang);
        }
        navigate("/dashboard", { replace: true });
      } else {
        const res = await api<LoginResponse>("/api/auth/signup", {
          method: "POST",
          body: {
            email: email.trim().toLowerCase(),
            password,
            site_name: siteName.trim(),
            timezone: detectTimezone(),
            // Whatever language they filled this form in is the language they
            // want their alerts in.
            lang: currentLanguage(),
          },
          skipAuth: true,
        });
        setSession(res.token, res.user);
        navigate("/onboarding", { replace: true });
      }
    } catch (err) {
      setError(t(authErrorKey(err)));
    } finally {
      setSubmitting(false);
    }
  }

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const submitLabel = submitting
    ? mode === "login"
      ? t("auth.submittingLogin")
      : t("auth.submittingSignup")
    : mode === "login"
      ? t("auth.submitLogin")
      : t("auth.submitSignup");

  const isSubmitDisabled = submitting || !email || !password || (mode === "signup" && !siteName.trim());

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex justify-end px-4 pt-4 sm:px-6">
        <LanguageSwitcher variant="compact" />
      </div>

      <main className="flex-1 flex items-center justify-center px-4 pt-2 pb-12">
        <div className="w-full max-w-[400px]">
          <div className="flex flex-col items-center text-center">
            <BrandMark size={48} />
            <p className="mt-4 text-[22px] leading-7 font-bold tracking-[-0.01em] text-slate-900">WarnixAI</p>
            <p className="mt-1 text-sm text-slate-500">{t("auth.tagline")}</p>
          </div>

          <div className="mt-8 bg-white border border-slate-200 rounded-2xl shadow-[0_14px_40px_rgb(20_29_50/0.07)] p-6 sm:p-8">
            <h1 className="text-xl leading-7 font-bold text-slate-900">
              {mode === "login" ? t("auth.loginTitle") : t("auth.signupTitle")}
            </h1>
            {mode === "signup" && <p className="mt-1 text-sm leading-6 text-slate-500">{t("auth.signupLede")}</p>}

            <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
              {mode === "signup" && (
                <TextField
                  label={t("auth.siteName")}
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder={t("auth.siteNamePlaceholder")}
                  autoComplete="organization"
                  required
                />
              )}
              <TextField
                label={t("auth.email")}
                type="email"
                dir="ltr"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                autoComplete={mode === "login" ? "username" : "email"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@warnix.ai"
                inputClassName="text-start"
                required
              />
              <PasswordField
                label={t("auth.password")}
                hint={mode === "signup" ? t("auth.passwordHint") : undefined}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                minLength={mode === "signup" ? 8 : 1}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                revealLabel={t("auth.showPassword")}
                concealLabel={t("auth.hidePassword")}
                required
              />

              {error && <Notice tone="danger">{error}</Notice>}

              <Button type="submit" size="lg" className="w-full" disabled={isSubmitDisabled}>
                {submitLabel}
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-slate-600">
            {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
            <button
              type="button"
              onClick={() => switchMode(mode === "login" ? "signup" : "login")}
              className="font-semibold text-brand-600 hover:text-brand-700 hover:underline underline-offset-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              {mode === "login" ? t("auth.createAccount") : t("auth.backToLogin")}
            </button>
          </p>

          <p className="mt-10 text-center text-xs text-slate-400">
            WarnixAI v2 · {t("auth.build")} {import.meta.env.MODE}
          </p>
        </div>
      </main>
    </div>
  );
}
